import React, { ChangeEvent, Fragment, MouseEvent as ReactMouseEvent, useState, useCallback, useEffect } from "react";

import { AnswerTemplate } from "../../db/models/Answer";
import { OptionGroup, Option } from "../../db/models/Question";
import { useTranslate, useLanguage } from "../translation/frontend";
import ErrorMessage from "./ErrorMessage";
import TextInput from "./TextInput";
import { Translated, translateDbString } from "./Translated";
import AccordionIcon from "./icons/AccordionIcon";
import CheckIcon from "./icons/CheckIcon";

const inBrowser = typeof window !== "undefined";

interface QuestionOptionsProps {
  questionId: string;
  answers: AnswerTemplate[];
  optionGroups: OptionGroup[];
  inputType: "radio" | "checkbox";
  required: boolean;
  onInvalid: (option?: Option) => void;
  onValid: (option?: Option) => void;
}

export default function QuestionOptions({
  questionId,
  answers,
  optionGroups,
  inputType,
  required,
  onInvalid,
  onValid,
}: QuestionOptionsProps) {
  const language = useLanguage();
  const translate = useTranslate();
  const [isInvalid, setIsInvalid] = useState(false);

  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    answers.filter((answer) => answer.optionText).map((answer) => answer.optionId!),
  );
  const [openAccordions, setOpenAccordions] = useState<string[]>(
    optionGroups
      .filter((optionGroup) => optionGroup.options.some(({ id }) => selectedOptions.includes(id)))
      .map(({ id }) => id),
  );

  const toggleAccordion = useCallback(
    (groupId: string) => (ev: ReactMouseEvent) => {
      ev.preventDefault();
      setOpenAccordions(
        openAccordions.includes(groupId)
          ? openAccordions.filter((openAccordion) => openAccordion !== groupId)
          : openAccordions.concat([groupId]),
      );
    },
    [openAccordions],
  );

  const clearOtherBox = (optionId: string) => {
    const otherInput = document.getElementById(`${optionId}-other`) as HTMLInputElement | null;
    if (otherInput) otherInput.value = "";
  };

  const changeOption = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      if (inputType === "radio" && ev.currentTarget.checked) {
        for (const option of selectedOptions) clearOtherBox(option);
        setSelectedOptions([ev.currentTarget.id]);
        setIsInvalid(false);
        onValid();
      } else if (inputType === "checkbox") {
        if (!ev.currentTarget.checked) clearOtherBox(ev.currentTarget.id);
        setSelectedOptions(
          ev.currentTarget.checked
            ? [...selectedOptions, ev.currentTarget.id]
            : selectedOptions.filter((id) => id !== ev.currentTarget.id),
        );
        setIsInvalid(false);
        onValid();
      }
    },
    [inputType, onValid, selectedOptions],
  );

  useEffect(() => {
    if (!required) return;
    const firstOption = optionGroups[0]?.options[0]?.id;
    if (!firstOption) return;
    // TypeScript doesn't like using refs here, but this is fine:
    const firstInput = document.getElementById(firstOption) as HTMLInputElement;
    if (!firstInput) return;
    firstInput.setCustomValidity(selectedOptions.length > 0 ? "" : translate("required"));
  }, [selectedOptions, required, optionGroups, translate]);

  const handleOnInvalid = useCallback(() => {
    setIsInvalid(true);
    onInvalid();
  }, [onInvalid]);

  return (
    <>
      {isInvalid ? <ErrorMessage type={inputType} id={questionId} /> : null}
      <div className="options">
        {optionGroups.map((optionGroup, i) => (
          <div
            key={optionGroup.id}
            className={`options__group ${optionGroup.collapsible ? "options__group--collapsible" : ""} ${
              openAccordions.includes(optionGroup.id) ? "options__group--open" : ""
            }`}
          >
            {optionGroup.groupName ? (
              <h3
                className={`options__subtitle ${optionGroup.collapsible ? "options__subtitle--collapsible" : ""}`}
                onClick={optionGroup.collapsible ? toggleAccordion(optionGroup.id) : undefined}
              >
                <Translated translations={optionGroup.groupNameTranslations}>{optionGroup.groupName}</Translated>
                {optionGroup.collapsible ? (
                  <button className="options__collapse">
                    <AccordionIcon />
                  </button>
                ) : null}
              </h3>
            ) : null}
            <div className="options__options">
              {optionGroup.options.map((option) => {
                const value = translateDbString(option.valueTranslations, language) ?? option.value;
                const answer = answers.find((answer) => answer.optionId === option.id);
                const selected = selectedOptions.includes(option.id);
                const otherDescription =
                  translateDbString(option.otherDescriptionTranslations, language) ?? option.otherDescription;
                return (
                  <Fragment key={option.id}>
                    <div className="options__option">
                      <div className="options__label">
                        <input
                          type={inputType}
                          className={`form-control options__check options__${inputType}`}
                          id={option.id}
                          name={questionId}
                          value={JSON.stringify({ optionId: option.id, optionText: option.value })}
                          defaultChecked={selected}
                          onChange={changeOption}
                          required={required && inputType === "radio"}
                          onInvalid={handleOnInvalid}
                        />
                        {inputType === "checkbox" ? <CheckIcon /> : null}
                        <div className="options__label-target">
                          <label
                            htmlFor={option.id}
                            aria-describedby={option.definition ? `options__label-hint-${option.id}` : undefined}
                          >
                            {value}
                            {option.suffix ? (
                              <span className="options__label-suffix">
                                {" "}
                                <Translated translations={option.suffixTranslations}>{option.suffix}</Translated>
                              </span>
                            ) : null}
                          </label>
                          {option.definition ? (
                            <div
                              className="options__label-hint content-styles"
                              id={`options__label-hint-${option.id}`}
                              dangerouslySetInnerHTML={{
                                __html: translateDbString(option.definitionTranslations, language) ?? option.definition,
                              }}
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                    {option.allowOther ? (
                      <div className={`options__other ${selected ? "is-visible" : ""}`}>
                        <label className="hidden" htmlFor={`${option.id}-other`}>
                          {otherDescription || translate("otherPleaseSpecify")}
                        </label>
                        <TextInput
                          type={option.otherType ?? "TEXT"}
                          id={`${option.id}-other`}
                          name={option.id}
                          placeholder={otherDescription}
                          defaultValue={answer?.freeText}
                          required={inBrowser && !option.otherOptional && selected}
                          characterLimit={option.otherCharacterLimit ?? null}
                          onInvalid={() => onInvalid(option)}
                          onValid={() => onValid(option)}
                        />
                      </div>
                    ) : null}
                  </Fragment>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
